import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import {
  CartAddItemEvent,
  CartApplyPromoCodeEvent,
  CartApplySubscriptionEvent,
  CartChangeQuantityEvent,
  CartEditItemEvent,
  CartRemoveItemEvent,
  CartRemovePromoCodeEvent,
  InsuranceEvent,
  InsuranceEventType,
  RequestProductQuoteEvent,
  ServiceStatusEvent,
  LegacyAddInsuranceListenerEvent,
  LegacyRemoveInsuranceListenerEvent,
  AddInsuranceActionListenerEvent,
  RemoveInsuranceActionListenerEvent,
  RemoveInsuranceBenefitsEvent,
  AddInsuranceBenefitsEvent,
  RequestBenefitPlanEvent,
  RequestInsuranceDataEvent,
} from '@typesApp/AppEvent'
import { addInsuranceActionListener, removeInsuranceActionListener } from '@redux/slices/insuranceMemberSlice'
import { useProductQuoteService } from './useProductQuoteService'
import { useInsuranceBenefitsUsage } from './useInsuranceBenefitsUsage'
import { useServiceStatus } from './useServiceStatus'
import { useBenefitPlanService } from './useBenefitPlanService'
import { useInsuranceDataService } from './useInsuranceDataService'
import { useOrderCompleteEventHandler } from './useOrderCompleteEventHandler'
import { useLoginEventHandler } from './useLoginEventHandler'
import { useLogoutEventHandler } from './useLogoutEventHandler'
import { useSessionErrorEventHandler } from './useSessionErrorEventHandler'
import { useCartBundleChangeEventHandler } from './useCartBundleChangeEventHandler'

const DEPRECATED_EVENTS = [
  'onCartAddItem',
  'onCartRemoveItem',
  'onCartEditItem',
  'onCartChangeQuantity',
  'onCartApplySubscription',
  'onCartApplyPromoCode',
  'onCartRemovePromoCode',
  'onAddSyncInsuranceListener',
  'onRemoveSyncInsuranceListener',
]

/**
 * Handle custom events from commerce app
 */
export const useAppEventListener = (): EventListenerOrEventListenerObject => {
  const dispatch = useDispatch()
  const serviceStatus = useServiceStatus()
  const handleLogin = useLoginEventHandler()
  const handleLogout = useLogoutEventHandler()
  const handleSessionError = useSessionErrorEventHandler()
  const handleOrderComplete = useOrderCompleteEventHandler()
  const handleCartBundleChange = useCartBundleChangeEventHandler()
  const { getBenefitPlan } = useBenefitPlanService()
  const { requestQuote } = useProductQuoteService()
  const { getInsuranceData } = useInsuranceDataService()
  const { addInsuranceBenefits, removeInsuranceBenefits } = useInsuranceBenefitsUsage()

  const eventHandlers = useMemo(() => {
    /**
     * To be removed once brand F/E integrations have removed all interceptor events
     */
    const handleCartEdit = (
      event:
        | CartAddItemEvent
        | CartRemoveItemEvent
        | CartEditItemEvent
        | CartChangeQuantityEvent
        | CartApplySubscriptionEvent
        | CartApplyPromoCodeEvent
        | CartRemovePromoCodeEvent
    ) => {
      // all cart update popups are disabled but still need to invoke
      // the callback until commerce apps remove the integration points
      typeof event?.payload === 'function' && event.payload()
    }

    const handleRequestProductQuote = (event: RequestProductQuoteEvent) => {
      const { request, handler } = event.payload
      if (typeof handler === 'function' && Array.isArray(request) && request.length) {
        requestQuote(request).then(result => {
          handler(result)
        })
      }
    }

    const handleServiceStatus = (event: ServiceStatusEvent) => {
      const { serviceType, handler } = event.payload
      if (serviceType && typeof handler === 'function') {
        const status = serviceStatus[serviceType]
        status && handler(status)
      }
    }

    const handleAddInsuranceActionListener = (
      event: AddInsuranceActionListenerEvent | LegacyAddInsuranceListenerEvent
    ) => {
      if (typeof event?.payload === 'function') {
        const payload = { listener: event.payload, isLegacy: event.type === 'onAddSyncInsuranceListener' }
        dispatch(addInsuranceActionListener(payload))
      }
    }

    const handleRemoveInsuranceActionListener = (
      event: RemoveInsuranceActionListenerEvent | LegacyRemoveInsuranceListenerEvent
    ) => {
      if (typeof event?.payload === 'function') {
        dispatch(removeInsuranceActionListener(event.payload))
      }
    }

    const handleAddInsuranceBenefits = (_event: AddInsuranceBenefitsEvent) => {
      addInsuranceBenefits()
    }

    const handleRemoveInsuranceBenefits = (_event: RemoveInsuranceBenefitsEvent) => {
      removeInsuranceBenefits()
    }

    const handleRequestBenefitPlan = (event: RequestBenefitPlanEvent) => {
      if (typeof event?.payload === 'function') {
        getBenefitPlan().then(response => event.payload(response))
      }
    }

    const handleRequestInsuranceData = (event: RequestInsuranceDataEvent) => {
      if (typeof event?.payload === 'function') {
        getInsuranceData().then(response => event.payload(response))
      }
    }

    return {
      onLogin: event => event.type === 'onLogin' && handleLogin(),
      onLogout: event => event.type === 'onLogout' && handleLogout(),
      onSessionError: event => event.type === 'onSessionError' && handleSessionError(event?.payload),
      onOrderComplete: event => event.type === 'onOrderComplete' && handleOrderComplete(),
      onCartBundleChange: event => event.type === 'onCartBundleChange' && handleCartBundleChange(),
      onCartAddItem: event => event.type === 'onCartAddItem' && handleCartEdit(event),
      onCartRemoveItem: event => event.type === 'onCartRemoveItem' && handleCartEdit(event),
      onCartEditItem: event => event.type === 'onCartEditItem' && handleCartEdit(event),
      onCartChangeQuantity: event => event.type === 'onCartChangeQuantity' && handleCartEdit(event),
      onCartApplySubscription: event => event.type === 'onCartApplySubscription' && handleCartEdit(event),
      onCartApplyPromoCode: event => event.type === 'onCartApplyPromoCode' && handleCartEdit(event),
      onCartRemovePromoCode: event => event.type === 'onCartRemovePromoCode' && handleCartEdit(event),
      onServiceStatus: event => event.type === 'onServiceStatus' && handleServiceStatus(event),
      onRequestProductQuote: event => event.type === 'onRequestProductQuote' && handleRequestProductQuote(event),

      // NOTE: 'sync' events are for backwards compatibility
      onAddSyncInsuranceListener: event =>
        event.type === 'onAddSyncInsuranceListener' && handleAddInsuranceActionListener(event),
      onRemoveSyncInsuranceListener: event =>
        event.type === 'onRemoveSyncInsuranceListener' && handleRemoveInsuranceActionListener(event),

      onAddInsuranceActionListener: event =>
        event.type === 'onAddInsuranceActionListener' && handleAddInsuranceActionListener(event),
      onRemoveInsuranceActionListener: event =>
        event.type === 'onRemoveInsuranceActionListener' && handleRemoveInsuranceActionListener(event),
      onAddInsuranceBenefits: event => event.type === 'onAddInsuranceBenefits' && handleAddInsuranceBenefits(event),
      onRemoveInsuranceBenefits: event =>
        event.type === 'onRemoveInsuranceBenefits' && handleRemoveInsuranceBenefits(event),
      onRequestBenefitPlan: event => event.type === 'onRequestBenefitPlan' && handleRequestBenefitPlan(event),
      onRequestInsuranceData: event => event.type === 'onRequestInsuranceData' && handleRequestInsuranceData(event),
    } as Record<InsuranceEventType, (event: InsuranceEvent) => void>
  }, [
    dispatch,
    getBenefitPlan,
    getInsuranceData,
    requestQuote,
    addInsuranceBenefits,
    removeInsuranceBenefits,
    handleOrderComplete,
    handleLogin,
    handleLogout,
    handleSessionError,
    handleCartBundleChange,
    serviceStatus,
  ])

  const eventListener = useCallback(
    (event: Event) => {
      if (!isCustomEvent(event)) return

      const insuranceEvent = event?.detail

      if (insuranceEvent?.type) {
        if (DEPRECATED_EVENTS.includes(insuranceEvent.type)) {
          console.debug('directbilling received deprecated app-event: ', insuranceEvent)
        } else {
          console.debug('directbilling received app-event: ', insuranceEvent)
        }
        eventHandlers[insuranceEvent.type]?.(insuranceEvent)
      }
    },
    [eventHandlers]
  )

  return eventListener
}

const isCustomEvent = (event: Event): event is CustomEvent<InsuranceEvent> => {
  return event instanceof CustomEvent
}
